/**
 * @fileoverview Share Portal API client module for external user access
 *
 * PUBLIC ENDPOINTS: External users access shared labs without Okta authentication.
 * Uses share session tokens stored in sessionStorage (not localStorage).
 */

import axios from 'axios'
import { getShareSessionToken } from './client'

/**
 * @typedef {Object} ShareInfo
 * @property {string} share_id - Share UUID
 * @property {string} lab_id - Prefixed lab ID (lab-xxx) for support reference
 * @property {string} lab_name - Lab name
 * @property {string} description - Share description
 * @property {string} expires_at - ISO timestamp of expiration
 * @property {boolean} is_active - Whether share is active
 * @property {boolean} requires_authentication - Always true
 */

/**
 * @typedef {Object} ShareAuthRequest
 * @property {string} username - Guacamole username
 * @property {string} password - Guacamole password
 */

/**
 * @typedef {Object} ShareAuthResponse
 * @property {string} session_token - Share session token
 * @property {string} share_id - Share UUID
 * @property {string} lab_name - Lab name
 * @property {string} expires_at - ISO timestamp when session expires
 */

/**
 * @typedef {Object} ShareVM
 * @property {string} id - VM ID
 * @property {string} name - VM name
 * @property {string} os - Operating system
 * @property {string} status - VM status (running, stopped, error)
 * @property {string} [ip_address] - VM IP address
 * @property {number} cpu - CPU count
 * @property {number} memory_gb - Memory in GB
 */

/**
 * @typedef {Object} ShareConsoleCredentials
 * @property {string} websocket_url - WebSocket URL with embedded encrypted token
 * @property {string} connection_id - VM connection identifier
 * @property {string} [vm_name] - VM name (if available)
 */

/**
 * Create a public axios instance without authentication
 * Used for unauthenticated endpoints (getShareInfo)
 */
const publicClient = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || '/api',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json'
  }
})

/**
 * Create an axios instance for authenticated share portal requests
 * Uses X-Share-Session header for authentication
 */
const shareClient = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || '/api',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json'
  }
})

// Request interceptor to add share session token
shareClient.interceptors.request.use(
  config => {
    const token = getShareSessionToken()
    if (token) {
      config.headers['X-Share-Session'] = token
    }
    return config
  },
  error => {
    return Promise.reject(error)
  }
)

// Response interceptor for error handling
shareClient.interceptors.response.use(
  response => response,
  async error => {
    // Handle 401 Unauthorized for share portal
    if (error.response?.status === 401) {
      // Clear share session token
      sessionStorage.removeItem('share_session_token')

      // Check if we're on the share portal page
      const path = window.location.pathname
      if (path.startsWith('/share/')) {
        // Stay on page to show login form
        // Don't redirect, just clear token and reject
      }
    }

    // Normalize error
    if (error.response) {
      return Promise.reject({
        message: error.response.data?.detail || error.response.data?.message || error.message,
        status: error.response.status,
        data: error.response.data
      })
    } else if (error.request) {
      return Promise.reject({
        message: 'No response from server. Please check your connection.',
        status: 0,
        data: null
      })
    } else {
      return Promise.reject({
        message: error.message || 'An unexpected error occurred',
        status: 0,
        data: null
      })
    }
  }
)

/**
 * Get public information about a share (no authentication required)
 *
 * @param {string} shareId - Share UUID
 * @returns {Promise<ShareInfo>} Share information
 * @example
 * const info = await getShareInfo('550e8400-e29b-41d4-a716-446655440000')
 * console.debug(`Lab: ${info.lab_name}`)
 * console.debug(`Expires: ${new Date(info.expires_at).toLocaleString()}`)
 */
export async function getShareInfo(shareId) {
  const response = await publicClient.get(`/v1/shares/${shareId}`)
  return response.data
}

/**
 * Authenticate with share credentials
 *
 * Returns a session token that should be stored in sessionStorage.
 * Use setShareSessionToken() from client.js to store it.
 *
 * @param {string} shareId - Share UUID
 * @param {ShareAuthRequest} credentials - Guacamole username and password
 * @returns {Promise<ShareAuthResponse>} Authentication response with session token
 * @throws {Error} 401 if credentials are invalid or share is expired
 * @example
 * try {
 *   const auth = await authenticateShare(shareId, {
 *     username: 'share-550e8400@labs.autolabs.com',
 *     password: 'provided-password'
 *   })
 *   // Store token using setShareSessionToken(auth.session_token)
 * } catch (error) {
 *   console.error('Authentication failed:', error.message)
 * }
 */
export async function authenticateShare(shareId, credentials) {
  const response = await publicClient.post(`/v1/shares/${shareId}/auth`, credentials)
  return response.data
}

/**
 * Get list of VMs for a shared lab (requires share session token)
 *
 * @param {string} shareId - Share UUID
 * @returns {Promise<ShareVM[]>} List of VMs in the shared lab
 * @throws {Error} 401 if session token is missing or invalid
 * @example
 * const vms = await getShareVMs('550e8400-e29b-41d4-a716-446655440000')
 * vms.forEach(vm => console.debug(`${vm.name}: ${vm.status}`))
 */
export async function getShareVMs(shareId) {
  const response = await shareClient.get(`/v1/shares/${shareId}/vms`)
  return response.data
}

/**
 * Control VM power state in a shared lab (requires share session token)
 *
 * External users can start, stop, or restart VMs but cannot delete them.
 *
 * @param {string} shareId - Share UUID
 * @param {string} vmId - VM ID
 * @param {'start'|'stop'|'restart'} action - Power action to perform
 * @returns {Promise<ShareVM>} Updated VM details
 * @throws {Error} 401 if session token is missing or invalid
 * @example
 * const vm = await controlShareVMPower(shareId, 'vm-123', 'start')
 * console.debug(`VM status: ${vm.status}`)
 */
export async function controlShareVMPower(shareId, vmId, action) {
  const response = await shareClient.post(`/v1/shares/${shareId}/vms/${vmId}/power`, { action })
  return response.data
}

/**
 * Get console access credentials for a VM in a shared lab (requires share session token)
 *
 * Returns WebSocket connection information for native guacamole-common-js integration.
 * The websocket_url already contains the encrypted token embedded in the URL path,
 * so no additional query parameters are needed when connecting.
 *
 * @param {string} shareId - Share UUID
 * @param {string} vmId - VM ID
 * @returns {Promise<ShareConsoleCredentials>} WebSocket connection details
 * @throws {Error} 401 if session token is missing or invalid
 * @example
 * const { websocket_url, connection_id } = await getShareVMConsole(shareId, 'vm-123')
 * // websocket_url format: wss://console.autolabs.com/ws/{encrypted_token}
 * // Use this URL directly to initialize guacamole-common-js WebSocket tunnel
 * connect(websocket_url)
 */
export async function getShareVMConsole(shareId, vmId) {
  const response = await shareClient.get(`/v1/shares/${shareId}/vms/${vmId}/console`)
  return response.data
}

// ============================================
// FILE MANAGEMENT ENDPOINTS FOR SHARE PORTAL
// ============================================

/**
 * @typedef {Object} ShareFile
 * @property {string} name - File or directory name
 * @property {string} path - Full path
 * @property {'file'|'directory'} type - Item type
 * @property {number} [size] - Size in bytes (files only)
 * @property {string} [modified] - Last modified timestamp
 */

/**
 * List files and directories in a shared lab
 *
 * @param {string} shareId - Share UUID
 * @param {string} [path='/'] - Directory path to list
 * @returns {Promise<ShareFile[]>} List of files and directories
 * @throws {Error} 401 if session token is missing or invalid
 */
export async function listShareFiles(shareId, path = '/') {
  const response = await shareClient.get(`/v1/shares/${shareId}/files`, {
    params: { path }
  })
  return response.data
}

/**
 * Get download URL for a file in a shared lab
 *
 * @param {string} shareId - Share UUID
 * @param {string} filePath - Full file path
 * @returns {Promise<{url: string, expires_at: string}>} Pre-signed download URL
 * @throws {Error} 401 if session token is missing or invalid
 */
export async function getShareFileDownloadUrl(shareId, filePath) {
  const response = await shareClient.get(`/v1/shares/${shareId}/files/download`, {
    params: { path: filePath }
  })
  return response.data
}

/**
 * Get upload URL for a file in a shared lab
 *
 * @param {string} shareId - Share UUID
 * @param {string} filePath - Full file path to upload to
 * @returns {Promise<{url: string, expires_at: string}>} Pre-signed upload URL
 * @throws {Error} 401 if session token is missing or invalid
 */
export async function getShareFileUploadUrl(shareId, filePath) {
  const response = await shareClient.post(`/v1/shares/${shareId}/files/upload`, {
    path: filePath
  })
  return response.data
}

/**
 * Create a directory in a shared lab
 *
 * @param {string} shareId - Share UUID
 * @param {string} dirPath - Directory path to create
 * @returns {Promise<void>}
 * @throws {Error} 401 if session token is missing or invalid
 */
export async function createShareDirectory(shareId, dirPath) {
  await shareClient.post(`/v1/shares/${shareId}/files/mkdir`, {
    path: dirPath
  })
}

/**
 * Delete a file or directory in a shared lab
 *
 * @param {string} shareId - Share UUID
 * @param {string} path - Path to delete
 * @returns {Promise<void>}
 * @throws {Error} 401 if session token is missing or invalid
 */
export async function deleteShareFile(shareId, path) {
  await shareClient.delete(`/v1/shares/${shareId}/files`, {
    params: { path }
  })
}
